import { createForm, type RenderParams } from '@seek/forms-ui';
import { FormActionButtons } from '@seek/profile-edit-ui';
import { structuredData } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { Stack, Heading } from 'braid-design-system';
import { useState } from 'react';

import type { DirtyFormProps } from 'src/components/FormDrawer/FormDrawer';
import { useOnFormDirtyChange } from 'src/components/FormDrawer/useOnFormDirtyChange';
import { useEnterReadMode } from 'src/components/Router/hooks';
import { useAppConfig } from 'src/modules/appConfig';
import type { Location2 } from 'src/types/graphql';

import { GroupedPreferredLocations } from '../GroupedPreferredLocations';
import { usePreferredLocations, useUpdatePreferredLocations } from '../data';
import { removeLocationFromList } from '../utils';

import translations from './.vocab';
import { AddLocation, type AutosuggestItem } from './AddLocation/AddLocation';
import { duplicatePreferredLocations } from './AddLocation/validation';
import { LocationItem } from './LocationItem/LocationItem';

export type EditedLocation = Location2 & {
  updateType: 'ADD' | 'DELETE';
};

const mapFormStateToProps = ({
  form: { dirty: isFormDirty },
}: {
  form: {
    dirty: boolean;
  };
}) => ({
  isFormDirty,
});

type MappedProps = ReturnType<typeof mapFormStateToProps>;

export interface FormValues {
  preferredLocations: Location2[];
  preferredLocation: AutosuggestItem;
}

type FormProps = RenderParams<FormValues> & MappedProps;

const { FormProvider, useField } = createForm<FormValues, MappedProps>(
  'preferred-locations-form',
  {
    formType: 'braid',
  },
);

export const EditPreferredLocationForm = ({
  isFormDirty,
  handleSubmit,
  onFormDirtyChange,
  resetForm,
}: FormProps & DirtyFormProps) => {
  const { t } = useTranslations(translations);
  const {
    zone,
    language: languageCode,
    locale: countryCode,
    isUnifiedSite,
  } = useAppConfig();

  useOnFormDirtyChange({ onFormDirtyChange, dirty: isFormDirty });

  const enterReadMode = useEnterReadMode('preferred-locations');
  const { updatePreferredLocations } = useUpdatePreferredLocations();

  const [editedLocations, setEditedLocations] = useState<EditedLocation[]>([]);

  const { value: listedPreferredLocations, onChange } = useField({
    id: 'preferredLocations',
  });

  const preferredLocationField = useField({
    id: 'preferredLocation',
    validators: [
      duplicatePreferredLocations(t('Location already exists')),
      structuredData,
    ],
    validatorProps: {
      locations: listedPreferredLocations,
      dataLabel: t('location'),
      dataIdentifierName: 'value',
    },
  });

  const addPreferredLocation = (addedLocation: Location2) => {
    setEditedLocations((oldEditedLocations) => [
      ...oldEditedLocations,
      {
        ...addedLocation,
        updateType: 'ADD',
      },
    ]);

    const newLocations = [...listedPreferredLocations, addedLocation];

    onChange(newLocations);
  };

  const removePreferredLocation = (removedLocation: Location2) => {
    setEditedLocations((oldEditedLocations) => [
      ...oldEditedLocations,
      {
        ...removedLocation,
        updateType: 'DELETE',
      },
    ]);

    const newLocations = removeLocationFromList(
      removedLocation,
      listedPreferredLocations,
    );

    onChange(newLocations);
  };

  const locationItem = (location: Location2): React.ReactElement => (
    <LocationItem
      key={`${location.id}`}
      location={location}
      onRemoveLocation={removePreferredLocation}
    />
  );

  const handleOnSubmit = () => {
    if (isFormDirty) {
      const preferredLocations = listedPreferredLocations.map((location) => ({
        id: location.id,
      }));

      updatePreferredLocations(
        {
          input: { preferredLocations },
          countryCode,
          languageCode,
          zone,
          isUnifiedSite,
        },
        {
          newLocations: listedPreferredLocations,
          locationTrackingList: editedLocations,
        },
      );
    }
    enterReadMode();
  };

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)}>
      <Stack space="large" data={{ automation: 'preferred-locations' }}>
        <AddLocation
          addNewLocation={addPreferredLocation}
          locations={listedPreferredLocations}
          zone={zone}
          preferredLocationField={preferredLocationField}
          resetForm={resetForm}
        />
        <Heading level="4">{t('Added locations')}</Heading>
        {listedPreferredLocations.length > 0 && (
          <GroupedPreferredLocations
            preferredLocations={listedPreferredLocations}
            locationItem={locationItem}
            dataAutomationSuffix="edit"
          />
        )}
        <FormActionButtons
          cardName={t('Preferred locations')}
          dataAutomationSuffix="preferred-locations"
          onCancel={enterReadMode}
        />
      </Stack>
    </form>
  );
};

export const EditPreferredLocation = (dirtyFormProps: DirtyFormProps) => {
  const { preferredLocations } = usePreferredLocations();

  return (
    <FormProvider
      mapFormStateToProps={mapFormStateToProps}
      initialValues={{ preferredLocations, preferredLocation: { text: '' } }}
    >
      {(formProps) => (
        <EditPreferredLocationForm {...dirtyFormProps} {...formProps} />
      )}
    </FormProvider>
  );
};
